import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, FileText, Users, Package, CreditCard,
  Receipt, BarChart3, Settings, FileDown, Calculator, Eye, Landmark, ClipboardList, Truck
} from 'lucide-react'
import { useUserMode } from '../contexts/UserModeContext'
import { useAuth } from '../contexts/AuthContext'

const adminLinks = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/invoices', icon: FileText, label: 'Invoices' },
  { to: '/estimates', icon: ClipboardList, label: 'Estimates' },
  { to: '/delivery-challans', icon: Truck, label: 'Delivery Challans' },
  { to: '/credit-notes', icon: FileDown, label: 'Credit Notes' },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/payments', icon: CreditCard, label: 'Payments' },
  { to: '/expenses', icon: Receipt, label: 'Expenses' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/bank', icon: Landmark, label: 'Bank Reconciliation' },
  { to: '/settings', icon: Settings, label: 'Settings' },
]

const staffLinks = [
  { to: '/', icon: LayoutDashboard, label: 'Home' },
  { to: '/expenses', icon: Receipt, label: 'My Expenses' },
  { to: '/invoices/new', icon: FileText, label: 'Create Invoice' },
  { to: '/invoices', icon: FileText, label: 'My Invoices' },
]

// Restricted invoice-creator: raise invoices + download their PDFs only. No financials.
const creatorLinks = [
  { to: '/invoices/new', icon: FileText, label: 'Create Invoice' },
  { to: '/invoices', icon: FileText, label: 'My Invoices' },
]

export default function Sidebar() {
  const { mode, toggleMode, isAccountant } = useUserMode()
  const { user, loading, isAdmin, isInvoiceCreator } = useAuth()
  const links = isAdmin ? adminLinks : isInvoiceCreator ? creatorLinks : staffLinks

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <img src="/osteo3d-logo.png" alt="Osteo3d" style={{ height: 28, width: 'auto' }} />
          <span className="font-semibold text-gray-900">Osteo3d Books</span>
        </div>
      </div>

      {/* User info */}
      {loading ? (
        <div className="px-4 py-3 border-b border-gray-100 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-28 mb-1.5" />
          <div className="h-3 bg-gray-100 rounded w-36 mb-1.5" />
          <div className="h-4 bg-gray-100 rounded w-12" />
        </div>
      ) : user ? (
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
          <p className="text-xs text-gray-400 truncate">{user.email}</p>
          <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
            isAdmin ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {user.role}
          </span>
        </div>
      ) : null}

      <nav className="flex-1 py-2 overflow-y-auto">
        {loading ? (
          <div className="space-y-1 px-4 py-1 animate-pulse">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5">
                <div className="h-4 w-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded" style={{ width: `${60 + (i % 3) * 20}px` }} />
              </div>
            ))}
          </div>
        ) : links.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to + label}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-brand-50 text-brand-700 border-r-2 border-brand-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Mode toggle — only for admins */}
      {isAdmin && (
        <div className="border-t border-gray-200 p-3">
          <button
            onClick={toggleMode}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-xs font-medium transition-all hover:bg-gray-50"
            title={isAccountant ? 'Switch to Simple view' : 'Switch to Accountant view'}
          >
            <div className={`relative w-8 h-[18px] rounded-full transition-colors ${isAccountant ? 'bg-brand-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform ${isAccountant ? 'left-[17px]' : 'left-[2px]'}`} />
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              {isAccountant ? <Calculator className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              <span>{isAccountant ? 'Accountant' : 'Simple'} view</span>
            </div>
          </button>
        </div>
      )}
    </aside>
  )
}
